"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Loader2 } from "lucide-react";
import { groupActionsByCategory } from "@/lib/actions/action-categories";
import type { DetailAction, BaseEntity } from "@/components/shared/UniversalDetailPage/types/detailPage";

// ============================================================================
// TYPES
// ============================================================================

interface MobileActionsDropdownProps<T extends BaseEntity> {
  item: T;
  visibleActions: DetailAction<T>[];
  loadingActions: Set<string>;
  onAction: (action: DetailAction<T>) => void;
  className?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Mobile layout: all visible actions collapsed into a single full-width dropdown.
 */
export function MobileActionsDropdown<T extends BaseEntity>({
  item,
  visibleActions,
  loadingActions,
  onAction,
  className = "",
}: MobileActionsDropdownProps<T>) {
  const renderItem = (action: DetailAction<T>) => {
    const label = typeof action.label === "function" ? action.label(item) : action.label;
    const isDisabled =
      action.disabled && typeof action.disabled === "function" && action.disabled(item);
    return (
      <DropdownMenuItem
        key={action.id}
        onSelect={() => onAction(action)}
        disabled={isDisabled}
        className={`cursor-pointer ${action.variant === "destructive" ? "text-red-600 focus:text-red-600" : ""}`}
      >
        <div className="flex items-center gap-2">
          {loadingActions.has(action.id) && <Loader2 className="h-4 w-4 animate-spin" />}
          {label}
        </div>
      </DropdownMenuItem>
    );
  };

  // Group by category so the mobile "Actions" menu shows the same labeled
  // sections as the right-click menu. Destructive is pinned last, no header.
  const groups = groupActionsByCategory(visibleActions);
  const dangerItems = groups
    .filter((g) => g.category === "destructive")
    .flatMap((g) => g.items);
  const nonDangerGroups = groups.filter((g) => g.category !== "destructive");

  return (
    <div className={`w-full flex justify-center ${className}`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="w-full max-w-xs justify-between">
            Actions
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="center"
          className="w-full min-w-[200px] max-h-[400px] overflow-y-auto"
        >
          {nonDangerGroups.map((group, groupIndex) => (
            <React.Fragment key={group.category}>
              {groupIndex > 0 && <DropdownMenuSeparator />}
              <DropdownMenuLabel className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {group.label}
              </DropdownMenuLabel>
              {group.items.map(renderItem)}
            </React.Fragment>
          ))}

          {dangerItems.length > 0 && (
            <>
              {nonDangerGroups.length > 0 && <DropdownMenuSeparator />}
              {dangerItems.map(renderItem)}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
